import { BaseQueryFn, FetchArgs, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_TAG_TYPES_ARRAY, API_VERSION } from 'constants/common';
import { loggedOut, updateSTLTokens } from 'containers/auth/authSlice';
import { RootState } from 'store/reducers';
import { LocalStorage } from './storage';
import { showNotifier } from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';
import { isPermissionDenied } from 'utils/apiUtils';
import { Mutex } from 'async-mutex';

export type ExtraOptions = {
  showToast?: boolean;
  failure?: string;
  success?: string;
  showCustomMessage?: boolean;
};

interface RefreshMutationResponse {
  idToken: string;
  accessToken: string;
  tokenType: string;
  validToken: boolean;
}

const openEndpoints = ['refresh', 'login', 'downloadEvidence'];

const handleNotifier = (apiResult, baseApi, apiExtraOptions: ExtraOptions) => {
  const { showToast = false, failure = '', success = '' } = apiExtraOptions ?? {};
  if (showToast) {
    if (apiResult?.error?.status && !isPermissionDenied(apiResult?.error?.status, apiResult?.error?.data?.message)) {
      baseApi.dispatch(
        showNotifier({
          message: {
            primaryMessage: failure || 'Something went wrong'
          },
          type: NotifierTypes.ERROR,
          showClose: true
        })
      );
      if (window?.clevertap) {
        window.clevertap.event.push('Webapp_API_error_toast_shown', {
          errorData: JSON.stringify(apiResult?.error),
          api: JSON.stringify(baseApi.endpoint)
        });
      }
    } else if (apiResult?.meta?.response?.status === 200 && success) {
      baseApi.dispatch(
        showNotifier({
          message: {
            primaryMessage: success || 'Success'
          },
          type: NotifierTypes.SUCCESS,
          showClose: true
        })
      );
    }
  }
};

const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_API_ENDPOINT}/`,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const authState = (getState() as RootState).rootReducer.auth;
    const idToken = authState.idToken ?? LocalStorage.get('idToken');
    const isAuthenticated = authState.isAuthenticated ?? LocalStorage.get('isAuthenticated');
    const hasInitiatedSignIn = authState.hasInitiatedSignIn ?? LocalStorage.get('hasInitiatedSignIn');
    const hasInitiatedSignUp = authState.hasInitiatedSignUp ?? LocalStorage.get('hasInitiatedSignUp');
    const refreshToken = authState.refreshToken ?? LocalStorage.get('refreshToken');

    if (
      !openEndpoints.includes(endpoint) &&
      refreshToken &&
      idToken &&
      (isAuthenticated || hasInitiatedSignIn || hasInitiatedSignUp || authState.isWebView)
    ) {
      // IdToken token plays the role of an access token
      headers.set('Authorization', `Bearer ${idToken}`);
    }
    return headers;
  }
  // credentials: 'include' // allows the server to set cookies
});

// !TODO use async mutex to prevent multiple refresh calls
// !TODO use generic toast to show service related erros
// We wrap baseQuery with this fn
// const baseQueryWithReauth: BaseQueryFn<
//   string | FetchArgs,
//   unknown, //Result
//   any, //Error
//   ExtraOptions,
//   object //Meta
// > = async (args, api, extraOptions) => {
//   try {
//     let result = await baseQuery(args, api, extraOptions);
//     const resultErrorData = result?.error?.data as any;
//     if (result?.error && result?.error?.status === 401 && resultErrorData?.message !== 'WRONG_PASSWORD') {
//       // Try to get new token
//       const refreshResult = await baseQuery(
//         {
//           url: `${API_VERSION.V1}/auth/refresh`,
//           method: 'POST',
//           body: { refreshToken: LocalStorage.get('refreshToken') }
//         },
//         { ...api, endpoint: 'refresh' },
//         extraOptions
//       );
//       const refreshResponse = refreshResult.data as RefreshMutationResponse;
//       if (refreshResult.data) {
//         // Update the access token in authSlice and storage
//         LocalStorage.set('idToken', refreshResponse.idToken);
//         LocalStorage.set('accessToken', refreshResponse.accessToken);
//         api.dispatch(updateSTLTokens(refreshResponse));
//         // Retry the initial query
//         result = await baseQuery(args, api, extraOptions);
//       } else {
//         // !TODO clear redux store: dispatch a global reset action
//         api.dispatch(loggedOut());
//       }
//     }
//     handleNotifier(result, api, extraOptions);
//     return result;
//   } catch (error) {
//     // !TODO how do we handle errors?
//     // eslint-disable-next-line no-console
//     console.log(error);
//   }
// };

const mutex = new Mutex();

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown, //Result
  any, //Error
  ExtraOptions,
  object //Meta
> = async (args, api, extraOptions) => {
  try {
    let result = await baseQuery(args, api, extraOptions);
    const resultErrorData = result?.error?.data as any;
    if (result?.error && result?.error?.status === 401 && resultErrorData?.message !== 'WRONG_PASSWORD') {
      if (!mutex.isLocked()) {
        const release = await mutex.acquire();

        try {
          const refreshResult = await baseQuery(
            {
              url: `${API_VERSION.V1}/auth/refresh`,
              method: 'POST',
              body: { refreshToken: LocalStorage.get('refreshToken') }
            },
            { ...api, endpoint: 'refresh' },
            extraOptions
          );
          const refreshResponse = refreshResult.data as RefreshMutationResponse;
          if (refreshResult.data) {
            // Update the access token in authSlice and storage
            LocalStorage.set('idToken', refreshResponse.idToken);
            LocalStorage.set('accessToken', refreshResponse.accessToken);
            api.dispatch(updateSTLTokens(refreshResponse));
            // Retry the initial query
            result = await baseQuery(args, api, extraOptions);
          } else {
            // !TODO clear redux store: dispatch a global reset action
            api.dispatch(loggedOut());
          }
        } finally {
          release();
        }
      } else {
        await mutex.waitForUnlock();
        result = await baseQuery(args, api, extraOptions);
      }
    }
    handleNotifier(result, api, extraOptions);
    if (result.error) {
      if (window?.clevertap) {
        window.clevertap.event.push('Webapp_API_error', {
          errorData: JSON.stringify(result?.error),
          api: JSON.stringify(api.endpoint)
        });
      }
    }

    return result;
  } catch (error) {
    // !TODO how do we handle errors?
    // eslint-disable-next-line no-console
    console.log(error);
    if (window?.clevertap) {
      window.clevertap.event.push('Webapp_API_error', {
        errorData: JSON.stringify(error),
        api: JSON.stringify(api.endpoint)
      });
    }
  }
};

export const baseApi = createApi({
  reducerPath: 'baseApi',
  tagTypes: API_TAG_TYPES_ARRAY,
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true
});
